import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close,
  ArrowUpward,
  ArrowDownward,
  Star,
  Bolt,
  SentimentSatisfied,
  Schedule,
  Event,
  CalendarMonth,
  SortByAlpha,
  Check,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setSortOrder } from "../../../redux/actions/user";
import BrainIcon from "../../../assets/brain.svg";
import useUserAPIClient from "../../../api/user/hooks/useUserAPIClient";

const SORT_OPTIONS = [
  {
    id: "adaptive",
    label: "Adaptive",
    description: "Intelligent sorting based on your mindset",
    useBrainIcon: true,
    isAdaptive: true,
  },
  {
    id: "importance",
    ascLabel: "Least important first",
    descLabel: "Most important first",
    icon: Star,
  },
  {
    id: "difficulty",
    ascLabel: "Easiest first",
    descLabel: "Hardest first",
    icon: Bolt,
  },
  {
    id: "fun",
    ascLabel: "Least fun first",
    descLabel: "Most fun first",
    icon: SentimentSatisfied,
  },
  {
    id: "created",
    ascLabel: "Oldest first",
    descLabel: "Newest first",
    icon: Schedule,
  },
  {
    id: "deadline",
    ascLabel: "Soonest deadline",
    descLabel: "Furthest deadline",
    icon: Event,
  },
  {
    id: "schedule",
    ascLabel: "Scheduled soonest",
    descLabel: "Scheduled furthest",
    icon: CalendarMonth,
  },
  {
    id: "name",
    ascLabel: "A → Z",
    descLabel: "Z → A",
    icon: SortByAlpha,
  },
];

const TaskSortDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { sortOrder } = useSelector((state) => state.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { setSortPreference } = useUserAPIClient();

  const handleSortChange = (sortValue) => {
    dispatch(setSortOrder(sortValue));
    void setSortPreference(sortValue);
  };

  const getCurrentSortLabel = () => {
    if (sortOrder === "adaptive") return "Adaptive";
    const [optionId, direction] = sortOrder?.split("-") || [];
    const option = SORT_OPTIONS.find((o) => o.id === optionId);
    if (!option) return "None";
    return direction === "asc" ? option.ascLabel : option.descLabel;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Sort Tasks
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose how to order your task list
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{ color: "grey.500" }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Current Sort Display */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: "#004a3c", // $primary-dark
            color: "white !important",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              mb: "0 !important",
              color: "white !important",
              fontSize: "1rem",
            }}
          >
            Current:
          </Typography>
          <Chip
            label={getCurrentSortLabel()}
            size="medium"
            sx={{
              bgcolor: "#197b68", // $primary
              color: "white !important",
              fontWeight: "bold",
              border: "1px solid #ffa600", // $yellow
              fontSize: "0.95rem",
            }}
          />
        </Box>

        <List
          className="scroller"
          sx={{
            py: 0,
            maxHeight: isMobile ? "50vh" : "60vh",
            overflow: "auto",
          }}
        >
          {/* Adaptive Option - Special styling */}
          {SORT_OPTIONS.filter((o) => o.isAdaptive).map((option) => {
            const isSelected = sortOrder === "adaptive";

            return (
              <ListItem key={option.id} disablePadding>
                <ListItemButton
                  onClick={() => handleSortChange("adaptive")}
                  sx={{
                    py: isMobile ? 1.5 : 3,
                    px: isMobile ? 2 : 3,
                    bgcolor: "#197b68", // $primary
                    color: "white",
                    gap: isMobile ? 1 : 2,
                    "&:hover": {
                      bgcolor: "#197b68",
                      opacity: 0.9,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "auto" }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        bgcolor: "#004a3c", // $primary-dark
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "50%",
                          backgroundColor: "#ffffff !important",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={BrainIcon}
                          alt="Brain"
                          style={{
                            width: 32,
                            height: 32,
                          }}
                        />
                      </Box>
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    sx={{ ml: 1 }}
                    primary={
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{ color: "white !important", mb: "0 !important" }}
                      >
                        {option.label}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(255,255,255,0.8) !important" }}
                      >
                        {option.description}
                      </Typography>
                    }
                  />
                  {isSelected && (
                    <Box
                      sx={{
                        width: isMobile ? 48 : 32,
                        height: isMobile ? 48 : 32,
                        borderRadius: "50%",
                        bgcolor: "#004a3c", // $primary-dark
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                      }}
                    >
                      <Check
                        sx={{
                          color: "white !important",
                          fontSize: isMobile ? 20 : 24,
                        }}
                      />
                    </Box>
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}

          <Divider />

          <Box sx={{ px: 2, py: 1.5, bgcolor: "grey.50", textAlign: "center" }}>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              fontWeight="medium"
              sx={{ mb: "0 !important" }}
            >
              Manual Sort Options
            </Typography>
          </Box>

          {/* Regular Sort Options */}
          {SORT_OPTIONS.filter((o) => !o.isAdaptive).map((option) => {
            const Icon = option.icon;
            const isDescSelected = sortOrder === `${option.id}-desc`;
            const isAscSelected = sortOrder === `${option.id}-asc`;
            const isSelected = isDescSelected || isAscSelected;

            return (
              <ListItem
                key={option.id}
                disablePadding
                sx={{
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    px: isMobile ? 1 : 2,
                    py: isMobile ? 1 : 1.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: isMobile ? 18 : 22,
                        color: isSelected ? "#197b68" : "grey.500",
                      }}
                    />
                    <Typography
                      variant={isMobile ? "caption" : "body2"}
                      fontWeight={isSelected ? "bold" : "normal"}
                      color={isSelected ? "#197b68" : "text.primary"}
                      sx={{ mb: "0 !important" }}
                    >
                      {option.id.charAt(0).toUpperCase() + option.id.slice(1)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexDirection: isMobile ? "column" : "row",
                    }}
                  >
                    <Chip
                      icon={<ArrowDownward sx={{ fontSize: 18 }} />}
                      label={option.descLabel}
                      size="medium"
                      onClick={() => handleSortChange(`${option.id}-desc`)}
                      variant={isDescSelected ? "filled" : "outlined"}
                      color={isDescSelected ? "primary" : "default"}
                      sx={{
                        flex: 1,
                        justifyContent: "flex-start",
                        py: isMobile ? 1.5 : 2,
                        "& .MuiChip-label": {
                          flex: 1,
                          fontSize: isMobile ? "0.75rem" : "0.85rem",
                        },
                        "& .MuiChip-icon": { fontSize: 16 },
                      }}
                    />
                    <Chip
                      icon={<ArrowUpward sx={{ fontSize: 18 }} />}
                      label={option.ascLabel}
                      size="medium"
                      onClick={() => handleSortChange(`${option.id}-asc`)}
                      variant={isAscSelected ? "filled" : "outlined"}
                      color={isAscSelected ? "primary" : "default"}
                      sx={{
                        flex: 1,
                        justifyContent: "flex-start",
                        py: isMobile ? 1.5 : 2,
                        "& .MuiChip-label": {
                          flex: 1,
                          fontSize: isMobile ? "0.75rem" : "0.85rem",
                        },
                        "& .MuiChip-icon": { fontSize: 16 },
                      }}
                    />
                  </Box>
                </Box>
              </ListItem>
            );
          })}
        </List>
      </DialogContent>

      <DialogActions sx={{ p: isMobile ? 1 : 2 }}>
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskSortDialog;
